import { CommonModule } from '@angular/common';
import { Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogService, DialogsModule, WindowCloseResult, WindowModule, WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { GridModule, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { PagerModule } from '@progress/kendo-angular-pager';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import { DeleteConfirmComponent } from '../../common-components/delete-confirm/delete-confirm.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Role, User } from '../../models/models.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { CasiCloseOptions, FormModes, AppEnities, blastKeys } from '../../common/constants.lib';
import { LoggerService } from '../../shared/logger.service';
import { UserDataService } from '../user-data.service';
import { ToastrService } from 'ngx-toastr';
import { faPlusCircle, faAddressCard, faEdit, faTrash, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { setTitle } from '../../common/utils.lib';
import { CasiRolesService } from '../casi-roles.service';
import { EditUserComponent } from '../edit-user/edit-user.component';
import { MeService } from '../../auth/me.service';

import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { SocketService } from '../../shared/socket.service';
import { BrowserModule } from '@angular/platform-browser';

const { USER } = AppEnities;

@Component({
  selector: 'casi-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    // BrowserModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    PagerModule,
    GridModule,
    MulticheckFilterComponent,
    // ErrorDialogModalComponent,
    // DeleteConfirmComponent,
    // EditUserComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit, OnDestroy {
  public view: Observable<User[]> = new Observable<User[]>();
  public rawData: User[] = [];
  public rolesData: Role[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: User[] = filterBy(this.rawData, this.filter);

  public currentUser: User = new User();
  public selectedRoles: any = {};
  public selectedRoleIds: Role[] = [];

  public formMode: FormModes = FormModes.NOTSET;
  public editWinOpen: boolean = false;
  public canUpdate: boolean = false;
  public canCreate: boolean = false;
  public canDestroy: boolean = false;

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  private showEmailInUse: boolean = false;

  private _windowRef: WindowRef | undefined;

  private _subs: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    public dataService: UserDataService,
    public rolesService: CasiRolesService,
    public faIcons: FaIconLibrary,
    private toastr: ToastrService,
    private windowService: WindowService,
    private dialogService: DialogService,
    private me: MeService,
    private socketService: SocketService
  ) {
    faIcons.addIcons(
      faPlusCircle, faAddressCard, faEdit,
      faTrash, faTriangleExclamation
    );
    faIcons.addIcons(...all);
  }

  public sort: SortDescriptor[] = [
    {
      field: "name",
      dir: "asc",
    },
  ];

  //#region Ng Functions

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    const { can_create, can_update, can_deactivate } = this.me.allICanDo(USER)
    this.canUpdate = can_update;
    this.canCreate = can_create;
    this.canDestroy = can_deactivate;
    this.dataService.query();
    this.rolesService.query();
    this._subs.push(this.dataService.subscribe(v => {
      this.logger.debug('ngOnInit:v: ', v);
      this.rawData = v;
      this.loadData();
    }));
    this._subs.push(this.rolesService.subscribe(roles => {
      this.logger.debug('ngOnInit:roles: ', roles);
      this.rolesData = roles;
    }));
    this._subs.push(this.socketService.onRefresh()
      .subscribe((args: any | unknown) => {
        this.logger.debug(`onRefresh: `, args);
        if (args && args.blastKey === blastKeys.USER_UPDATED) {
          this.dataService.query();
        }
      }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Grid functions

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    // this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.rawData, fieldName).map((item: any) => item[fieldName]).filter(v => v !== null && v !== undefined && v !== '');
  }

  //#endregion

  public showAddWindow(titleBar: TemplateRef<unknown>): void {
    this.logger.debug("showAddWindow...");
    this.closeWindowRef();
    this.currentUser = new User();
    this.formMode = FormModes.NEW;
    this.selectedRoleIds = [];
    this.logger.debug("showAddWindow:currentUser ", this.currentUser);
    this.showTitle = setTitle(this.formMode, 'USER DETAILS');
    this._windowRef = this.windowService.open({
      // title: setTitle(this.formMode, 'USER DETAILS'),
      titleBarContent: titleBar,
      content: EditUserComponent,
      width: 700,
      minWidth: 320
    });
    this.setAddEditInstanceInputs();

    this._windowRef.content.instance.submitForm.subscribe((formData: FormGroup) => {
      this.logger.debug('showAddWindow:formData: ', formData);
      this.handleWinRefClose(formData.value, this.formMode);
    });

    this._windowRef.result.subscribe(results => {
      this.logger.debug('showAddWindow:results: ', results);
      if (results instanceof WindowCloseResult) {
        this.closeWindowRef();
      }
    });

    this._windowRef.content.instance.closeForm.subscribe(() => {
      this.logger.debug('showAddWindow:closeForm');
      this.closeWindowRef();
    });
    this.editWinOpen = true;

  }

  public showEditWindow(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("showEditWindow...");
    this.closeWindowRef();
    this.currentUser = User.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.logger.debug("showEditWindow:currentUser ", this.currentUser);
    this.showTitle = setTitle(this.formMode, 'USER DETAILS');
    this._windowRef = this.windowService.open({
      // title: setTitle(this.formMode, 'USER DETAILS'),
      titleBarContent: titleBar,
      content: EditUserComponent,
      width: 700,
      minWidth: 320
    });
    this.getUserDetails();

    this._windowRef.content.instance.submitForm.subscribe((formData: FormGroup) => {
      this.logger.debug('showEditWindow:formData: ', formData);
      this.handleWinRefClose(formData.value, this.formMode);
    });

    this._windowRef.result.subscribe(results => {
      this.logger.debug('showEditWindow:results: ', results);
      if (results instanceof WindowCloseResult) {
        this.closeWindowRef();
      }
    });

    this._windowRef.content.instance.closeForm.subscribe(() => {
      this.logger.debug('showEditWindow:closeForm');
      this.closeWindowRef();
    });
    this.editWinOpen = true;
  }

  //#region Private functions

  private setAddEditInstanceInputs(): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.user = this.currentUser;
      // inputs.userId = this.currentUser.id?.toString();
      inputs.roles = this.rolesData;
      inputs.selectedRoleIds = this.selectedRoleIds;
      inputs.mode = this.formMode;
      this.logger.debug('setAddEditInstanceInputsinputs: ', inputs);
    }
  }

  private getUserDetails(): void {
    this.selectedRoleIds = [];
    // this.rolesService.getUserRoles((this.currentUser.id as number))
    this.rolesService.getUserRoles((this.currentUser.id as number))
      .then(userRoles => {
        this.logger.debug('getUserDetails:userRoles: ', userRoles);
        if (userRoles && userRoles.length > 0) {
          this.selectedRoleIds = userRoles.map(ur => {
            return (this.rolesData.find(rd => rd.id === ur.role_id) as Role);
          });
        }
        this.currentUser.addRoles(this.selectedRoleIds);
        this.logger.debug('getUserDetails:userRoles:selectedRoleIds: ', this.selectedRoleIds);
        this.setAddEditInstanceInputs();
      })
      .catch(err => {
        this.logger.error('getUserDetails:getUserRoles:error: ', err);
      })
  }

  private closeWindowRef(): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    this.editWinOpen = false;
  }

  private openErrorDailog(): void {
    const dialogRef = this.dialogService.open({
      content: ErrorDialogModalComponent,
      minWidth: 320,
      width: 750,
      cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    contentRef.title = "ERROR UPDATING EMAIL ADDRESS";
    contentRef.content = "That email address has already been used in another user account.";
    contentRef.buttonText = "Close and Retry";
  }

  public openConfirmDailog(dataItem: any): void {
    this.logger.debug("openConfirmDailog: ", dataItem);
    this.currentUser = dataItem;
    const dialogRef = this.dialogService.open({
      content: DeleteConfirmComponent,
      minWidth: 320,
      width: 750,
      // cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as DeleteConfirmComponent;
    contentRef.title = "CONFIRM DELETE";
    contentRef.content = `Delete User - <b>${this.currentUser.first_name} ${this.currentUser.last_name}</b>?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ", this.currentUser);
        this.handleRemove(this.currentUser);
      }
    });
  }

  private handleWinRefClose(formData: any, mode: FormModes): void {
    this.logger.debug('handleWinRefClose...', formData);
    let toSubmit = formData;
    let uRoles = (formData.userRoles as Role[]).map(ur => ur.id);

    if (mode === FormModes.EDIT) {
      this.dataService.updateUser(toSubmit, uRoles)
        .then(resp => {
          this.showEmailInUse = false;
          this.notify('User Updated');
          // this.toastr.success('User Updated');
          this.closeWindowRef();
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('handleWinRefClose:', err);
          this.showEmailInUse = err.status === 409;
          if (this.showEmailInUse) {
            this.openErrorDailog();
          } else {
            this.notify('Error saving user', true);
            // this.toastr.error('Error saving user');
          }
        });
    } else {
      this.dataService.createUser(toSubmit, uRoles)
        .then(resp => {
          this.showEmailInUse = false;
          // this.toastr.success('User Added');
          this.notify('User Added');
          this.toastr.info(`Invitation Sent to ${toSubmit.email}`);
          this.closeWindowRef();
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('handleWinRefClose:', err);
          this.showEmailInUse = err.status === 409;
          if (this.showEmailInUse) {
            this.openErrorDailog();
          } else {
            this.notify('Error saving user', true);
            // this.toastr.error('Error saving user');
          }
        });
    }
  }

  private loadData(): void {
    this.logger.debug('loadData...');
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.logger.debug('loadData...gridData', this.gridData);
    this.pageSize = this.gridData.length;//findNearestNumber(this.sizes, this.gridData.length);
  }

  private handleRemove(args: any): void {
    if (args && !args.id) {
      this.logger.error('handlRemove: No id given');
    }

    this.dataService.deleteUser(args.id)
      .then(resp => {
        this.logger.debug('handleRemove:resp: ', resp);
        this.notify("User Deleted");
        this.dataService.query();
      })
      .catch(err => {
        this.notify("Error Deleting User", true);
        this.logger.error('handleRemove:err: ', err);
      });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  toggleActive(dataItem: any) {
    this.logger.debug('toggleActive:dataItem: ', dataItem);
    this.currentUser = dataItem;
    this.dataService.setActive(dataItem.id, dataItem.active)
      .then(() => {
        this.toastr.success(!this.currentUser.active ? "User Activated" : "User Deactivated", "Success");

      })
      .catch(e => {
        this.logger.error(`toggleActive:error: `, e);
        this.toastr.error('Error updating user', 'Error');

      })
      .finally(() => this.dataService.query());
  }

  //#endregion

}
